import React, {useEffect, useState} from 'react';
import {
  Button,
  Divider,
  Flex,
  Text,
  Tooltip,
  FormControl,
  Textarea,
  RadioGroup,
  VStack,
} from '@chakra-ui/react';
import {ScrollableFlex} from '../../utils/scrolling';
import {WrapperFlex} from '../../utils/scrolling';
import {StyledRadioCard} from '../../utils/StyledRadioCard';
import {Box} from '@chakra-ui/react';
import {
  LeftColumnContent,
  RightColumnContent,
  TwoColumnGrid,
} from './components';
import {AgentBuilderPageIndicator} from './AgentBuilderPageIndicator';
import {useAgentNavigation} from './AgentBuilderNav.state';
import {useFieldDefinitionEditorStore} from './AiFieldDefinitionEditor.state';
import {
  useAgentBuilderStore,
  useAgentBuilderServerState,
} from './AgentBuilder.state';
import {BuilderFooter} from './BuilderFooter';
import {BuilderPreviewTable} from './BuilderPreviewTable';
import {AgentBuilderPreviewButton} from './AgentBuilderPreviewButton';
import {useIsEnrichingAgent} from '../../../hooks/api/enrichment';
import {
  AiTeamSizeCountries,
  AiTeamSizeFieldDefinitionConfig,
  isAiFieldDefinition,
} from '../../../shared/fields/enrichment';
import {useCustomer} from '../../../hooks/api/metadata';
import {StepContainer} from './StepContainer';
import {ConfigFormLabel} from './ConfigFormLabel';
import {KeywordsInput} from './KeywordsInput';
import {Information} from '@carbon/icons-react';
import {getAiFieldModel} from '../../../shared/agent';
import {RoleSearchCountries} from '../../../shared/countries';
import Select from 'react-select';
import {useIsKeyplayAdmin} from '../../../hooks/api/user';

export const AgentBuilderEditTeamSizeFieldStep = () => {
  const {id: agentId, status: agentStatus} = useAgentBuilderServerState();
  const {previewAccountIds} = useAgentBuilderStore();
  const {publishChanges, updateServerState, isSaveable} =
    useFieldDefinitionEditorStore();

  const fieldDefinitions = useCustomer().fieldDefinitions ?? [];
  const teamSizeField = fieldDefinitions
    .filter(isAiFieldDefinition)
    .find((fieldDefinition) => fieldDefinition.agentId?.equals(agentId));

  useEffect(() => {
    if (teamSizeField) {
      updateServerState({
        serverState: teamSizeField,
        updateMode: 'ifNoChanges',
      });
    }
  }, [teamSizeField, updateServerState]);

  const onNavigate = useAgentNavigation({
    mutation: publishChanges,
    saveOnBack: agentStatus === 'draft' && isSaveable(),
  });

  return (
    <StepContainer isSaveable={isSaveable()} onSaveMutation={publishChanges}>
      <TwoColumnGrid>
        <Flex
          borderRight="1px solid"
          borderColor="kgray.200"
          direction="column"
        >
          <Box position="relative" h="100%">
            <WrapperFlex>
              <ScrollableFlex p="0">
                <LeftColumnContent>
                  <AgentBuilderPageIndicator />
                  <TeamSizeFieldEditor />
                </LeftColumnContent>
              </ScrollableFlex>
            </WrapperFlex>
          </Box>

          <Divider />

          <BuilderFooter justifyContent="space-between">
            <Button
              isLoading={publishChanges.isPending}
              onClick={() => onNavigate('prev')}
              variant="ghost"
            >
              Back
            </Button>

            <Button
              colorScheme="kbuttonblue"
              isDisabled={!isSaveable()}
              isLoading={publishChanges.isPending}
              onClick={() => onNavigate('next')}
            >
              Continue to Review
            </Button>
          </BuilderFooter>
        </Flex>

        <RightColumnContent>
          <Flex alignItems="baseline" justifyContent="space-between" mt="-6px">
            <Text fontSize="xl">Preview</Text>
            <AgentBuilderPreviewButton accountIds={previewAccountIds} />
          </Flex>

          <Box flex="1" position="relative">
            <WrapperFlex>
              <ScrollableFlex p="0">
                <BuilderPreviewTable
                  accountIds={previewAccountIds}
                  fieldDefinition={teamSizeField}
                />
              </ScrollableFlex>
            </WrapperFlex>
          </Box>
        </RightColumnContent>
      </TwoColumnGrid>
    </StepContainer>
  );
};

const CountryModeCard = ({
  title,
  value,
  children,
  isDisabled,
}: {
  isDisabled?: boolean;
  title: string;
  value: string;
  children?: React.ReactNode;
}) => (
  <StyledRadioCard value={value} isDisabled={isDisabled} px={4} py={4}>
    <Box ml="2">
      <Text fontSize="md">{title}</Text>
      {children && <Box mt={3}>{children}</Box>}
    </Box>
  </StyledRadioCard>
);

const Countries = ({
  countries,
  onChange,
  isDisabled,
}: {
  isDisabled?: boolean;
  countries: AiTeamSizeCountries;
  onChange: (countries: AiTeamSizeCountries) => void;
}) => {
  const countryOptions = RoleSearchCountries.map((country) => ({
    value: country,
    label: country,
  }));

  const selectedCountries = countries.type === 'specific' ? countries.list : [];
  const [savedSpecificCountries, setSavedSpecificCountries] = useState<
    typeof selectedCountries
  >([]);

  useEffect(() => {
    if (countries.type === 'specific') {
      setSavedSpecificCountries(countries.list);
    }
  }, [countries]);

  const handleCountryModeChange = (newType: AiTeamSizeCountries['type']) => {
    const newCountries =
      newType === 'all'
        ? {type: 'all' as const}
        : newType === 'usUkAndCanada'
          ? {type: 'usUkAndCanada' as const}
          : {type: 'specific' as const, list: savedSpecificCountries};

    onChange(newCountries);
  };

  return (
    <Box mt="1">
      <ConfigFormLabel>
        <Flex align="center" gap={2}>
          Countries
          <Tooltip
            label="The Agent will limit its search to these countries."
            placement="right"
            hasArrow
          >
            <Information size={16} />
          </Tooltip>
        </Flex>
      </ConfigFormLabel>

      <RadioGroup
        value={countries.type}
        onChange={handleCountryModeChange}
        isDisabled={isDisabled}
      >
        <VStack spacing="3" mt={3} align="stretch">
          <CountryModeCard
            title="All Countries - Recommended"
            value="all"
            isDisabled={isDisabled}
          />
          <CountryModeCard
            title="US, UK, & Canada"
            value="usUkAndCanada"
            isDisabled={isDisabled}
          />
          <CountryModeCard
            title="Specific Countries"
            value="specific"
            isDisabled={isDisabled}
          >
            {countries.type === 'specific' && (
              <Box
                width="100%"
                onClick={(e) => {
                  e.preventDefault();
                }}
              >
                <Select
                  isDisabled={isDisabled}
                  isMulti
                  options={countryOptions}
                  value={selectedCountries.map((country) => ({
                    value: country,
                    label: country,
                  }))}
                  onChange={(selected) =>
                    onChange({
                      type: 'specific',
                      list: selected
                        ? selected.map((option) => option.value)
                        : [],
                    })
                  }
                  placeholder="Select countries..."
                  menuPlacement="auto"
                />
              </Box>
            )}
          </CountryModeCard>
        </VStack>
      </RadioGroup>
    </Box>
  );
};

const defaultConfig = {
  fieldType: 'teamSize' as const,
  model: getAiFieldModel(),
  analysisFields: [],
  keywords: [],
  exclude: [],
  countries: {type: 'all'} as const,
};

const TeamSizeFieldEditor = () => {
  const {config, setConfig} = useFieldDefinitionEditorStore();
  const {id: agentId, status} = useAgentBuilderServerState();
  const {data: isEnrichingAgent} = useIsEnrichingAgent({agentId});
  const isDisabled = status === 'published' || isEnrichingAgent;
  const isAdmin = useIsKeyplayAdmin();

  if (config && config.fieldType !== 'teamSize') {
    return null;
  }

  const updateConfig = (updates: Partial<AiTeamSizeFieldDefinitionConfig>) => {
    if (isDisabled) {
      return;
    }

    setConfig({
      ...(config ?? defaultConfig),
      ...updates,
    });
  };

  return (
    <Flex direction="column" gap="6">
      <Text fontSize="xl">Prompt your Team Size Estimator</Text>

      <FormControl>
        <ConfigFormLabel>
          <Flex align="center" gap={2}>
            Prompt
            <Tooltip
              label="It's best to phrase your prompt as a question about a single account, like “How many prompt engineers work at this company?”"
              placement="right"
              hasArrow
            >
              <Information size={16} />
            </Tooltip>
          </Flex>
        </ConfigFormLabel>
        <Textarea
          my={1}
          isDisabled={isDisabled}
          onChange={(e) => updateConfig({prompt: e.target.value})}
          rows={3}
          placeholder="Ask your agent a question about team sizes..."
          value={config?.prompt ?? ''}
        />
      </FormControl>
      <Divider />
      <KeywordsInput
        isDisabled={isDisabled}
        keywords={(config ?? defaultConfig).keywords}
        onChange={(keywords) => updateConfig({keywords})}
        label="Keywords"
        tooltipLabel="The Agent will use these title keywords to search and estimate team sizes."
        placeholder="Add a keyword..."
      />
      <Divider />
      {isAdmin && (
        <>
          <KeywordsInput
            isDisabled={isDisabled}
            keywords={(config ?? defaultConfig).exclude}
            onChange={(exclude) => updateConfig({exclude})}
            label="Exclusion Keywords"
            tooltipLabel="The Agent will exclude results containing these keywords."
            placeholder="Add an exclusion keyword..."
          />
          <Divider />
        </>
      )}
      <Countries
        isDisabled={isDisabled}
        countries={(config ?? defaultConfig).countries}
        onChange={(countries) => updateConfig({countries})}
      />
    </Flex>
  );
};
